<template>
  <b-card no-body class="started-completion">
    <div class="started-completion__header d-flex align-items-center px-3 py-3">
      <b-icon-exclamation-circle-fill
        variant="warning"
        font-scale="1.35"
        class="mr-3 flex-shrink-0"
      />
      <div>
        <h5 class="mb-0 font-weight-bold">
          {{ $t('order-details.completion.title') }}
        </h5>
        <small class="text-muted">{{ remainingLine }}</small>
      </div>
    </div>
    <div
      v-if="$slots['adjustment-action']"
      class="started-completion__adjustment d-flex align-items-start px-3 py-3"
    >
      <b-icon-pencil-square
        variant="secondary"
        font-scale="1.5"
        class="mr-3 mt-1 flex-shrink-0"
      />
      <div class="flex-grow-1">
        <div class="font-weight-bold">
          {{ $t('order-details.completion.adjustment-title') }}
        </div>
        <small class="d-block">
          {{ $t('order-details.completion.adjustment-hint') }}
        </small>
        <div class="started-completion__action">
          <slot name="adjustment-action" />
        </div>
      </div>
    </div>
    <b-list-group flush>
      <b-list-group-item
        v-for="step in steps"
        :key="step.key"
        class="d-flex align-items-start"
      >
        <component
          :is="step.iconComponent"
          :variant="step.iconVariant"
          font-scale="1.5"
          class="mr-3 mt-1 flex-shrink-0"
        />
        <div class="flex-grow-1">
          <div class="font-weight-bold d-flex align-items-center flex-wrap">
            {{ step.title }}
            <b-badge
              :class="[
                'ml-2',
                'text-uppercase',
                step.done
                  ? 'started-completion__badge--done'
                  : 'started-completion__badge--open',
              ]"
            >
              {{
                step.done
                  ? $t('order-details.completion.step-done')
                  : $t('order-details.completion.step-open')
              }}
            </b-badge>
          </div>
          <small class="text-muted d-block">{{ step.desc }}</small>
          <div v-if="!step.done" class="started-completion__action">
            <slot v-if="step.key === 'protocol'" name="protocol-action" />
            <slot
              v-else-if="step.key === 'signature'"
              name="signature-action"
            />
          </div>
        </div>
      </b-list-group-item>
    </b-list-group>
  </b-card>
</template>

<script>
import {
  BIconExclamationCircleFill,
  BIconCheckCircleFill,
  BIconFileEarmarkText,
  BIconPen,
  BIconPencilSquare,
} from 'bootstrap-vue';

export default {
  components: {
    BIconExclamationCircleFill,
    BIconCheckCircleFill,
    BIconFileEarmarkText,
    BIconPen,
    BIconPencilSquare,
  },
  props: {
    protocolDone: {
      type: Boolean,
      default: false,
    },
    signatureDone: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    steps() {
      return [
        {
          key: 'protocol',
          title: this.$t('order-details.completion.step-protocol-title'),
          desc: this.$t('order-details.completion.step-protocol-desc'),
          done: this.protocolDone,
          iconComponent: this.protocolDone
            ? 'b-icon-check-circle-fill'
            : 'b-icon-file-earmark-text',
          iconVariant: this.protocolDone ? 'success' : 'secondary',
        },
        {
          key: 'signature',
          title: this.$t('order-details.completion.step-signature-title'),
          desc: this.$t('order-details.completion.step-signature-desc'),
          done: this.signatureDone,
          iconComponent: this.signatureDone
            ? 'b-icon-check-circle-fill'
            : 'b-icon-pen',
          iconVariant: this.signatureDone ? 'success' : 'secondary',
        },
      ];
    },
    remainingNames() {
      const names = [];
      if (!this.protocolDone) {
        names.push(this.$t('order-details.completion.step-protocol-title'));
      }
      if (!this.signatureDone) {
        names.push(this.$t('order-details.completion.step-signature-title'));
      }
      return names;
    },
    remainingLine() {
      if (!this.remainingNames.length) {
        return this.$t('order-details.completion.all-done');
      }
      return this.$t('order-details.completion.remaining-line', {
        steps: this.remainingNames.join(', '),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.started-completion {
  border: 1px solid #e6e9ec;
  border-left: 3px solid #ff7043;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &__header {
    background-color: #fff;
    border-bottom: 1px solid #eef0f2;
  }

  &__adjustment {
    background-color: #f8f9fa;
    border-bottom: 1px solid #eef0f2;
  }

  ::v-deep .list-group-item {
    border-color: #eef0f2;
  }

  &__badge--open {
    background-color: #ff7043;
    color: #fff;
  }

  &__badge--done {
    background-color: #4caf50;
    color: #fff;
  }

  &__action {
    margin-top: 0.65rem;
  }
}
</style>
